import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import type { ContentProposal } from '@shared/contentProposal'
import React, { useRef, useState } from 'react'

import { type InlineProposalPlan, editedProposalMarkdown } from './inlineProposalPlan'
import MarkdownText from '../../../pages/book-editor/components/MarkdownText'

export interface InlineProposalReview {
  proposals: ContentProposal[]
  onAction: (
    item: ContentProposal,
    action: 'edit' | 'accept' | 'reject',
    markdown?: string,
    changeId?: string,
  ) => Promise<void>
}

/** This lives beside the contenteditable root, at a gap in its layout, never in its document state. */
export function InlineProposal({
  item,
  plan,
  blocked,
  onAction,
}: {
  item: ContentProposal
  plan: InlineProposalPlan
  blocked: boolean
  onAction: InlineProposalReview['onAction']
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Freeze the version being edited: a refresh must not silently overwrite someone else's draft.
  const editingItem = useRef(item)
  const editingPlan = useRef(plan)
  const act = async (action: 'edit' | 'accept' | 'reject') => {
    setBusy(true)
    setError('')
    try {
      await onAction(
        action === 'edit' ? editingItem.current : item,
        action,
        action === 'edit' ? editedProposalMarkdown(editingPlan.current, draft) : undefined,
      )
      setEditing(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'עדכון ההצעה נכשל')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Box
      data-inline-proposal={item.id}
      contentEditable={false}
      sx={{
        borderInlineStart: '2px solid var(--diff-insert-border)',
        ps: 1.25,
        '& .MuiTypography-root': { fontFamily: 'inherit' },
      }}
    >
      {editing ? (
        <TextField
          autoFocus
          multiline
          fullWidth
          minRows={2}
          maxRows={20}
          label="עריכת ההצעה בתוך הטקסט"
          value={draft}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          sx={{ '& textarea': { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' } }}
        />
      ) : plan.insertedMarkdown ? (
        <Box
          data-proposal-insertion
          sx={{
            bgcolor: 'var(--diff-insert-bg)',
            textDecoration: 'underline',
            textDecorationColor: 'var(--diff-insert-border)',
            overflowWrap: 'anywhere',
            '& p.MuiTypography-root': { fontSize: 'inherit', lineHeight: 'inherit', my: 0.75 },
          }}
        >
          <MarkdownText>{plan.insertedMarkdown}</MarkdownText>
        </Box>
      ) : (
        <Typography variant="caption">הצעה למחיקת הטקסט המסומן</Typography>
      )}
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        <Typography variant="caption" sx={{ opacity: 0.65, me: 1 }} title={item.reason}>
          הצעה · {item.source}
        </Typography>
        {editing ? (
          <>
            <Button size="small" disabled={busy} onClick={() => void act('edit')}>
              שמור הצעה
            </Button>
            <Button size="small" disabled={busy} onClick={() => setEditing(false)}>
              ביטול עריכה
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              disabled={busy || plan.stale || blocked || !item.canApply || item.status !== 'pending'}
              onClick={() => void act('accept')}
            >
              אשר
            </Button>
            <Button size="small" disabled={busy || item.status !== 'pending'} onClick={() => void act('reject')}>
              דחה
            </Button>
            <Button
              size="small"
              disabled={busy || item.status !== 'pending'}
              onClick={() => {
                editingItem.current = item
                editingPlan.current = plan
                setDraft(plan.insertedMarkdown)
                setEditing(true)
              }}
            >
              ערוך
            </Button>
          </>
        )}
      </Stack>
      {(plan.stale || blocked || !item.canApply) && (
        <Alert severity="warning" sx={{ my: 1 }}>
          {plan.stale
            ? 'הטקסט השתנה מאז הכנת ההצעה. יש להכין הצעה חדשה מול הנוסח הנוכחי.'
            : blocked
              ? 'יש הצעה נוספת באותו אזור. יש לטפל בה תחילה.'
              : 'ההצעה הוכנה בסביבת תוכן אחרת.'}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  )
}
